import { useMeeting } from '@videosdk.live/react-sdk'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Ellipsis, Info, PencilRuler } from 'lucide-react'
import { OutlinedButton } from '@/components/buttons/OutlinedButton'
import useIsTab from '@/hooks/useIsTab'
import useIsMobile from '@/hooks/useIsMobile'
import { debateToolModes, sideBarModes } from '@/utils/common'
import { useMeetingAppContext } from '@/MeetingAppContextDef'
import useMediaStream from '@/hooks/useMediaStream'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { useHall } from '@/contexts/HallProvider'
import { WinType } from '@db8/shared'
import {
  BallotBtn,
  ChatBTN,
  EvidenceBtn,
  ExportBtn,
  FlowNotesBtn,
  GuidebookBtn,
  JudgmentBtn,
  LeaveBTN,
  MicBTN,
  ParticipantsBTN,
  PollingBtn,
  RaiseHandBTN,
  RecordingBTN,
  SequenceBtn,
  TranscriptsBtn,
  WebCamBTN,
  SettingsBtn,
} from './Buttons'
import { MobileBottomBarDialog } from './MobileBottomBarDialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTurns } from '@/contexts/TurnsProvider'
import { toggleSidebarSmart } from '@/utils/sidebarToggle'

const DebateToolButtonKeys = {
  GUIDEBOOK: 'GUIDEBOOK',
  FLOW_NOTES: 'FLOW_NOTES',
  EVIDENCE: 'EVIDENCE',
  BALLOT: 'BALLOT',
  POLL: 'POLL',
} as const

type DebateToolButtonKey = keyof typeof DebateToolButtonKeys

export function BottomBar({ bottomBarHeight, setIsMeetingLeft }) {
  const {
    sideBarMode,
    setSideBarMode,
    secondSideBarMode,
    setSecondSideBarMode,
    selectedWebcam,
    setDebateToolMode,
  } = useMeetingAppContext()

  const { isOwner, transcriptions, judgment, hall, hallMetadata, runTour } = useHall()
  const { turnsComplete } = useTurns()
  const { isModerator, isSpectator } = useHallUsers()
  const mMeeting = useMeeting()
  const { getVideoTrack } = useMediaStream()
  const [shownWinCondition, setShownWinCondition] = useState(false)

  const showJudgmentBtn = useMemo(
    () => (isOwner && hallMetadata?.debateComplete && hall.winType === WinType.AI) || judgment,
    [isOwner, hallMetadata, judgment],
  )

  const showExportBtn = useMemo(() => hallMetadata?.debateComplete, [hallMetadata])

  const showPollingBtn = useMemo(() => {
    if (runTour) return true
    else if (
      [WinType.SIMPLEPOLL, WinType.EVALUATIONPOLL].includes(hall?.winType) &&
      hallMetadata?.pollingStarted
    )
      return true
  }, [runTour, hall, hallMetadata])

  const showTranscriptsBtn = useMemo(() => {
    const totalLength = Object.values(transcriptions).reduce((sum, arr) => sum + arr.length, 0)
    return totalLength > 0 || hallMetadata?.debateInitialized
  }, [hallMetadata])

  const toggleWebcam = async () => {
    let track
    if (!mMeeting?.localWebcamOn) {
      // @ts-ignore
      track = await getVideoTrack({
        webcamId: selectedWebcam.id,
      })
    }
    mMeeting.toggleWebcam(track)
  }

  const handleVoteHotkey = () => {
    if (
      hallMetadata?.pollingStarted &&
      [WinType.SIMPLEPOLL, WinType.EVALUATIONPOLL].includes(hall?.winType)
    )
      setDebateToolMode((prev) => (prev === debateToolModes.POLL ? null : debateToolModes.POLL))
  }

  useEffect(() => {
    if (hall && hallMetadata?.pollingStarted && !shownWinCondition) {
      if (!isOwner && hall?.winType === WinType.AI && judgment)
        setSideBarMode(sideBarModes.JUDGMENT)
      else if ([WinType.SIMPLEPOLL, WinType.EVALUATIONPOLL].includes(hall?.winType))
        setDebateToolMode(debateToolModes.POLL)
      else if (hall?.winType === WinType.BALLOT && isModerator)
        setDebateToolMode(debateToolModes.BALLOT)

      setShownWinCondition(true)
    }
  }, [hallMetadata?.pollingStarted, shownWinCondition, judgment, isModerator])

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return

      const key = e.key.toLowerCase()
      const isShift = e.shiftKey
      switch (key) {
        case 'c':
          toggleSidebarSmart(sideBarModes.CHAT, isShift)
          break
        case 's':
          toggleSidebarSmart(sideBarModes.SEQUENCE, isShift)
          break
        case 't':
          if (showTranscriptsBtn) toggleSidebarSmart(sideBarModes.TRANSCRIPTS, isShift)
          break
        case 'j':
          if (showJudgmentBtn) toggleSidebarSmart(sideBarModes.JUDGMENT, isShift)
          break
        case 'g':
          toggleSidebarSmart(sideBarModes.SETTINGS, isShift)
          break
        case 'p':
          toggleSidebarSmart(sideBarModes.PARTICIPANTS, isShift)
          break
        case 'm':
          mMeeting.toggleMic()
          break
        case ' ':
          mMeeting.toggleMic()
          break
        case 'w':
          toggleWebcam()
          break
        case 'f':
          setDebateToolMode((prev) =>
            prev === debateToolModes.FLOWNOTES ? null : debateToolModes.FLOWNOTES,
          )
          break
        case 'b':
          setDebateToolMode((prev) =>
            prev === debateToolModes.GUIDEBOOK ? null : debateToolModes.GUIDEBOOK,
          )
          break
        case 'o':
          handleVoteHotkey()
          break
        case 'e':
          setDebateToolMode((prev) =>
            prev === debateToolModes.EVIDENCE ? null : debateToolModes.EVIDENCE,
          )
          break
        case 'a':
          isModerator &&
            setDebateToolMode((prev) =>
              prev === debateToolModes.BALLOT ? null : debateToolModes.BALLOT,
            )
        default:
          break
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [sideBarMode, secondSideBarMode, sideBarModes, isOwner, setSideBarMode, setSecondSideBarMode])

  const toolTipEl = useRef(null)
  const isMobile = useIsMobile()
  const isTab = useIsTab()
  const [openSidebar, setOpenSidebar] = useState(false)
  const [openDebateTools, setOpenDebateTools] = useState(false)

  const debateToolKeys = useMemo(() => {
    const base = ['GUIDEBOOK', 'FLOW_NOTES', 'EVIDENCE']
    const extra = []
    if (isModerator) extra.push('BALLOT')
    if (
      [WinType.SIMPLEPOLL, WinType.EVALUATIONPOLL].includes(hall?.winType) &&
      hallMetadata?.pollingStarted
    ) {
      extra.push('POLL')
    }
    return [...base, ...extra]
  }, [hall?.winType, hallMetadata?.pollingStarted, isModerator])

  return isMobile || isTab ? (
    <div
      className="flex items-center justify-center"
      style={{
        height: `calc(${bottomBarHeight}px + env(safe-area-inset-bottom))`,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <MobileBottomBarDialog
        open={openDebateTools}
        handleClose={() => setOpenDebateTools(false)}
        items={debateToolKeys}
        renderItem={(key) => {
          switch (key) {
            case 'GUIDEBOOK':
              return <GuidebookBtn isMobile isTab closeDialog={() => setOpenDebateTools(false)} />
            case 'FLOW_NOTES':
              return <FlowNotesBtn isMobile isTab closeDialog={() => setOpenDebateTools(false)} />
            case 'EVIDENCE':
              return <EvidenceBtn isMobile isTab closeDialog={() => setOpenDebateTools(false)} />
            case 'BALLOT':
              return <BallotBtn isMobile isTab closeDialog={() => setOpenDebateTools(false)} />
            case 'POLL':
              return <PollingBtn isMobile isTab closeDialog={() => setOpenDebateTools(false)} />
            default:
              return null
          }
        }}
      />

      <div className="flex w-full justify-between">
        <OutlinedButton
          Icon={PencilRuler}
          onClick={() => setOpenDebateTools(true)}
          bgColor="bg-gray-200 dark:bg-gray-500 dark:bg-transparent mr-auto"
        />

        <div className="flex">
          <MicBTN />
          <WebCamBTN toggleWebcam={toggleWebcam} />
          <RecordingBTN />
          <LeaveBTN setIsMeetingLeft={setIsMeetingLeft} />
        </div>
        <OutlinedButton
          Icon={Ellipsis}
          onClick={() => setOpenSidebar(true)}
          bgColor="bg-gray-200 dark:bg-gray-500 dark:bg-transparent"
        />
      </div>

      <MobileBottomBarDialog
        open={openSidebar}
        handleClose={() => setOpenSidebar(false)}
        items={[
          'RAISE_HAND',
          'CHAT',
          'PARTICIPANTS',
          'SETTINGS',
          ...(showTranscriptsBtn ? ['TRANSCRIPTS'] : []),
          ...(showJudgmentBtn ? ['JUDGMENT'] : []),
          ...(isOwner ? ['SEQUENCE'] : []),
          ...(showExportBtn ? ['EXPORT'] : []),
        ]}
        renderItem={(key) => {
          switch (key) {
            case 'RAISE_HAND':
              return <RaiseHandBTN isMobile isTab />
            case 'CHAT':
              return <ChatBTN isMobile isTab />
            case 'PARTICIPANTS':
              return <ParticipantsBTN isMobile isTab />
            case 'SETTINGS':
              return <SettingsBtn isMobile isTab />
            case 'TRANSCRIPTS':
              return <TranscriptsBtn isMobile isTab />
            case 'JUDGMENT':
              return <JudgmentBtn isMobile isTab />
            case 'SEQUENCE':
              return <SequenceBtn isMobile isTab />
            case 'EXPORT':
              return <ExportBtn isMobile isTab />
            default:
              return null
          }
        }}
      />
    </div>
  ) : (
    <div className="md:flex lg:px-2 xl:px-6 pb-2 px-2 hidden">
      <div className="flex">
        <GuidebookBtn isMobile={isMobile} isTab={isTab} />
        <FlowNotesBtn isMobile={isMobile} isTab={isTab} />
        <EvidenceBtn isMobile={isMobile} isTab={isTab} />
        {isModerator && <BallotBtn isMobile={isMobile} isTab={isTab} />}
        {showPollingBtn && <PollingBtn isMobile={isMobile} isTab={isTab} />}
      </div>

      <div className="flex flex-1 items-center justify-center" ref={toolTipEl} id="controls">
        <RecordingBTN />
        <RaiseHandBTN isMobile={isMobile} isTab={isTab} />
        <MicBTN />
        <WebCamBTN toggleWebcam={toggleWebcam} />
        <LeaveBTN setIsMeetingLeft={setIsMeetingLeft} />
      </div>
      <div className="relative flex items-center justify-center" id="panels">
        <Tooltip>
          <TooltipTrigger asChild>
            <Info size="16px" className="cursor-pointer text-muted-foreground mr-4" />
          </TooltipTrigger>
          <TooltipContent className="w-72 text-sm text-left">
            <p className="text-xs">
              Hold <kbd>Shift</kbd> and click a tool button or <kbd>Hotkey</kbd> to open it in a
              second panel.
            </p>
          </TooltipContent>
        </Tooltip>

        {showExportBtn && <ExportBtn isMobile={isMobile} isTab={isTab} />}
        {showJudgmentBtn && <JudgmentBtn isMobile={isMobile} isTab={isTab} />}
        {showTranscriptsBtn && <TranscriptsBtn isMobile={isMobile} isTab={isTab} />}
        {!turnsComplete() && <SequenceBtn isMobile={isMobile} isTab={isTab} />}
        <ChatBTN isMobile={isMobile} isTab={isTab} />
        <ParticipantsBTN isMobile={isMobile} isTab={isTab} />
        {isOwner && <SettingsBtn isMobile={isMobile} isTab={isTab} />}
      </div>
    </div>
  )
}
